import type { FC, ReactNode } from "react";
import { createContext, useContext } from "react";
import type {
  FlowCard,
  ContentBlock,
  ImageContent,
  TextContent,
} from "../../api/dto/FlowCard";
import { resolveApiUrl } from "../../util/resolveApiUrl";

// Which language card previews (and, by default, new selections in the content-block editor modal)
// display — editor-scoped, so it doesn't have to be threaded as a prop through every level of the
// column/card tree (which is already several levels deep for the move/delete handlers).
export const DisplayLanguageContext = createContext<string>("nl");
export const useDisplayLanguage = () => useContext(DisplayLanguageContext);

// Renders every content block in order for the current display language — the real content, not a
// truncated snippet, so the swimlane overview doubles as a full preview of what's on the card.
export const CardContentPreview: FC<{ card: FlowCard; language: string }> = (props) => {
  const blocks = props.card.contentBlocks
    .map((block) => ({ block, node: contentBlockPreview(block, props.language) }))
    .filter(
      (entry): entry is { block: ContentBlock; node: ReactNode } => entry.node !== null,
    );

  if (blocks.length === 0) return <>Geen content</>;

  return (
    <>
      {blocks.map(({ block, node }) => (
        <div key={block.id}>{node}</div>
      ))}
    </>
  );
};

function contentBlockPreview(block: ContentBlock, language: string): ReactNode | null {
  if (block.type === "title") {
    const text = (block.content as TextContent)[language];
    if (!text) return null;
    return <div className={"text-xs font-semibold text-slate-700"}>{text}</div>;
  }
  if (block.type === "text") {
    const html = (block.content as TextContent)[language];
    if (!html) return null;
    return (
      <div
        className={"rich-text-content text-xs text-slate-600"}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored rich text from the same trust boundary as the editor itself, not arbitrary user input
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  if (block.type === "image") {
    const image = (block.content as ImageContent)[language];
    if (!image) return null;
    return (
      <img
        src={resolveApiUrl(image.url)}
        alt={""}
        className={"max-h-32 object-contain border border-slate-100 rounded"}
      />
    );
  }
  return null;
}
